import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { scoreDrill } from '@core/scoring/drill';
import { newSeed, shuffled } from './shuffle';
import type {
  Attempt, DrillDoc, DrillItem, DrillKey, DrillKeyItem, DrillResult, OrgConfig,
} from './types';

export type DrillPhase = 'loading' | 'answering' | 'feedback' | 'scored' | 'error';

/** Where the score on screen came from (§B: preview now, record shortly after). */
export type ScoreSource = 'preview' | 'server' | 'diverged';

export interface Choice {
  id: string;
  text: string;
}

export interface TurnRecord {
  nodeId: string;
  classificationSelected: string;
  explainTapped: boolean;
  msElapsed: number;
}

/**
 * The drill engine (§12.1). A drill is a single-node item with no `next`, no
 * sentiment and no branching: stimulus -> classify -> feedback -> next item.
 *
 * It reuses the attempt document, the scoring trigger and the rollups unchanged
 * (§12.1), so the only thing this hook does at the end is write the choices the
 * rep made. It never computes the score — the server does, and that is what
 * makes the record defensible.
 */
export function useDrillSession(
  orgId: string,
  uid: string,
  drillId: string,
  config: OrgConfig,
  salesRole: string | null
) {
  const [drill, setDrill] = useState<DrillDoc | null>(null);
  const [key, setKey] = useState<DrillKey | null>(null);
  const [phase, setPhase] = useState<DrillPhase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [turns, setTurns] = useState<TurnRecord[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [explainTapped, setExplainTapped] = useState(false);
  const [result, setResult] = useState<DrillResult | null>(null);
  const [scoreSource, setScoreSource] = useState<ScoreSource>('preview');
  const [attemptId, setAttemptId] = useState<string | null>(null);

  const seed = useMemo(newSeed, [drillId]);
  const startedAt = useRef(new Date().toISOString());
  const itemShownAt = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const drillSnap = await getDoc(doc(db, `orgs/${orgId}/scenarios/${drillId}`));
        if (!drillSnap.exists()) throw new Error('missing drill');
        if (cancelled) return;
        setDrill(drillSnap.data() as DrillDoc);

        // The answer key is readable only for practice content (§8.5, §9.3).
        // Without it there is no immediate feedback, which is exactly the
        // summative behavior (§14.4) — so a denied read is not an error.
        try {
          const keySnap = await getDoc(doc(db, `orgs/${orgId}/scenarioKeys/${drillId}`));
          if (!cancelled && keySnap.exists()) setKey(keySnap.data() as DrillKey);
        } catch {
          if (!cancelled) setKey(null);
        }

        if (!cancelled) { setPhase('answering'); itemShownAt.current = Date.now(); }
      } catch (err) {
        console.error('could not load drill', err);
        if (!cancelled) { setError('That exercise could not be loaded.'); setPhase('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, drillId]);

  const item: DrillItem | null = drill?.items[index] ?? null;
  const keyItem: DrillKeyItem | null =
    (key?.items.find((i: DrillKeyItem) => i.id === item?.id) ?? null);

  const personaById = useMemo(
    () => new Map(config.personas.map((p) => [p.id, p])),
    [config.personas]
  );

  /**
   * Choice labels come from the pack: persona names for a persona drill, the
   * org's objection taxonomy for an objection drill, authored text otherwise.
   * The engine holds no client-specific content (§1.1).
   */
  const choices: Choice[] = useMemo(() => {
    if (!item) return [];
    const raw: Choice[] = item.choices
      ? item.choices.map((c) => ({ id: c.id, text: c.text }))
      : (item.choiceIds ?? []).map((id) => ({
          id,
          text: personaById.get(id)?.name ?? humanise(id),
        }));
    // §14.1 — option order is shuffled every attempt, from the stored seed.
    return shuffled(raw, seed, item.id);
  }, [item, personaById, seed]);

  const answer = useCallback((choiceId: string) => {
    if (!item || phase !== 'answering') return;
    setSelected(choiceId);
    setExplainTapped(false);
    setTurns((prev) => [...prev, {
      nodeId: item.id,
      classificationSelected: choiceId,
      explainTapped: false,
      msElapsed: Date.now() - itemShownAt.current,
    }]);
    setPhase('feedback');
  }, [item, phase]);

  // §10.6 — log the tap. A rep who never opens the explanation after a bad
  // choice is not learning, and it surfaces in reporting.
  const tapExplain = useCallback(() => {
    setExplainTapped(true);
    setTurns((prev) => prev.map((t, i) =>
      i === prev.length - 1 ? { ...t, explainTapped: true } : t));
  }, []);

  const submit = useCallback(async (finalTurns: TurnRecord[]) => {
    const attempt: Attempt = {
      uid,
      orgId,
      scenarioId: drillId,
      type: 'drill',
      stage: drill?.stage ?? '',
      salesRole,
      mode: drill?.mode ?? 'practice',
      contentVersion: drill?.contentVersion ?? config.contentVersion,
      shuffleSeed: seed,
      startedAt: startedAt.current,
      submittedAt: new Date().toISOString(),
      turns: finalTurns,
    };

    // Score locally so the rep sees their result the moment they finish, with
    // no round trip. This is a preview, not the record: the same function runs
    // on the server against the same key, and `result` is replaced by whatever
    // the server wrote. The client cannot make a score true — it can only
    // predict one, and the security rules see to it that the prediction has no
    // authority (§9.5, §9.9, §9.11).
    if (key) {
      setResult(scoreDrill(attempt, key, config));
      setScoreSource('preview');
    }
    setPhase('scored');

    try {
      const ref = await addDoc(collection(db, `orgs/${orgId}/attempts`), attempt);
      setAttemptId(ref.id);
    } catch (err) {
      console.error('could not submit attempt', err);
      setError('Your answers could not be submitted. Check your connection and try again.');
      setPhase('error');
    }
  }, [orgId, uid, drillId, drill, salesRole, seed, config, key]);

  const next = useCallback(() => {
    if (!drill) return;
    const last = index >= drill.items.length - 1;
    setSelected(null);
    setExplainTapped(false);
    if (last) { void submit(turns); return; }
    setIndex((i) => i + 1);
    setPhase('answering');
    itemShownAt.current = Date.now();
  }, [drill, index, turns, submit]);

  // The server scores the attempt and appends `result` (§8.6). That record is
  // the authority; the preview above is replaced by it. A disagreement is a
  // defect, not a tolerance, so it is surfaced rather than smoothed over.
  useEffect(() => {
    if (!attemptId) return;
    const unsubscribe = onSnapshot(
      doc(db, `orgs/${orgId}/attempts/${attemptId}`),
      (snap) => {
        const scored = snap.data()?.result as DrillResult | undefined;
        if (!scored) return;
        setResult((preview) => {
          if (preview && diverges(preview, scored)) {
            console.error('client preview disagreed with the server score', { preview, scored });
            setScoreSource('diverged');
          } else {
            setScoreSource('server');
          }
          return scored;
        });
        setPhase('scored');
      },
      (err) => {
        console.error('could not read the scored attempt', err);
        setError('Your answers were saved, but the score could not be confirmed.');
      }
    );
    return unsubscribe;
  }, [attemptId, orgId]);

  return {
    drill, key, item, keyItem, choices, index, phase, error, selected,
    explainTapped, result, scoreSource, turns, answer, next, tapExplain,
    total: drill?.items.length ?? 0,
    tells: keyItem ? tellsFor(keyItem, personaById) : [],
  };
}

/**
 * Whether the preview and the server's record actually disagree. Compared on
 * the decision and the number a rep is shown; `scoredAt` and the item order
 * legitimately differ.
 */
function diverges(preview: DrillResult, scored: DrillResult): boolean {
  const value = (r: DrillResult) => r.personaScore ?? r.classificationScore ?? 0;
  return preview.passed !== scored.passed
    || Math.abs(value(preview) - value(scored)) > 1e-9
    || preview.correctCount !== scored.correctCount
    || preview.severeConfusionCount !== scored.severeConfusionCount;
}

const humanise = (id: string) =>
  id.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/**
 * §12.2 — feedback names the specific tells the rep should have caught, drawn
 * from `persona.tells`. `tellRefs` selects the ones this item demonstrates;
 * with none authored, all of the persona's tells apply.
 */
function tellsFor(
  keyItem: DrillKeyItem,
  personaById: Map<string, { tells: string[] }>
): string[] {
  const persona = personaById.get(keyItem.preferredId);
  if (!persona) return [];
  if (!keyItem.tellRefs?.length) return persona.tells;
  return keyItem.tellRefs
    .map((i) => persona.tells[i])
    .filter((t): t is string => typeof t === 'string');
}
